import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../../App';
import { Building2, FileText, Download, Calendar, Users, Clock, AlertTriangle, ChevronDown, ChevronRight, User } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatTimeRange } from '../../utils/dateTime';

const STATUS_BADGE = {
  attended: 'bg-emerald-50 text-emerald-700',
  no_show: 'bg-red-50 text-red-700',
  approved: 'bg-blue-50 text-blue-700',
  pending: 'bg-amber-50 text-amber-700',
  declined: 'bg-red-50 text-red-600',
  rejected: 'bg-red-50 text-red-700',
  edited: 'bg-blue-50 text-blue-700',
};

const PartnerReports = () => {
  const { user, logout } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [reportType, setReportType] = useState('opportunities');
  const [expandedOpps, setExpandedOpps] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      const res = await axios.get(`${API}/partner/reports/data?${params.toString()}`);
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [dateRange.start, dateRange.end]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleOpp = (oppId) => {
    setExpandedOpps(prev => ({ ...prev, [oppId]: !prev[oppId] }));
  };

  const exportToCSV = () => {
    if (!data) return;
    let csvContent = '';
    let filename = '';

    if (reportType === 'opportunities') {
      filename = `${data.organization_name}_opportunities_detail_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      csvContent = 'Opportunity,Date,Time,Location,Capacity,Signups,Attended,No-Shows,Total Hours,Status,Volunteer Name,Volunteer Email,Volunteer Status,Hours\n';
      data.opportunities.forEach(opp => {
        if (opp.participants.length === 0 && opp.time_volunteers.length === 0) {
          csvContent += `"${opp.title}","${opp.date}","${opp.start_time}-${opp.end_time}","${opp.location}",${opp.capacity},${opp.signups_count},${opp.attended_count},${opp.no_show_count},${opp.total_hours},"${opp.status}","","","",""\n`;
        }
        opp.participants.forEach(p => {
          csvContent += `"${opp.title}","${opp.date}","${opp.start_time}-${opp.end_time}","${opp.location}",${opp.capacity},${opp.signups_count},${opp.attended_count},${opp.no_show_count},${opp.total_hours},"${opp.status}","${p.name}","${p.email}","${p.signup_status}",${p.hours_credited || 0}\n`;
        });
        opp.time_volunteers.forEach(tv => {
          csvContent += `"${opp.title}","${opp.date}","","","","","","",${tv.total_hours || 0},"","${tv.name}","${tv.email}","time_entry_${tv.status}",${tv.total_hours || 0}\n`;
        });
      });
    } else if (reportType === 'hours') {
      filename = `${data.organization_name}_hours_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      csvContent = 'Volunteer Name,Email,Opportunity,Date,Status,Hours Credited\n';
      data.signups.forEach(signup => {
        csvContent += `"${signup.user_name}","${signup.user_email}","${signup.opportunity_title}","${signup.opportunity_date}","${signup.status}",${signup.hours_credited || 0}\n`;
      });
    } else {
      filename = `${data.organization_name}_time_entries_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      csvContent = 'Volunteer Name,Email,Opportunity,Check In,Check Out,Hours,Status\n';
      data.time_entries.forEach(entry => {
        const ci = entry.check_in_time ? format(new Date(entry.check_in_time), 'MMM d yyyy h:mm a') : '';
        const co = entry.check_out_time ? format(new Date(entry.check_out_time), 'MMM d yyyy h:mm a') : '';
        csvContent += `"${entry.volunteer_name}","${entry.volunteer_email}","${entry.opportunity_title || 'General'}","${ci}","${co}",${entry.total_hours || 0},"${entry.status}"\n`;
      });
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report downloaded');
  };

  const summary = data?.summary || {};

  return (
    <div className="min-h-screen bg-[#FBF4EA]" data-testid="partner-reports">
      {/* Navigation */}
      <nav className="bg-white border-b-2 border-[#E9DDCF]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Building2 className="w-6 h-6 text-[#8B2E3C]" />
              <div>
                <div className="font-bold text-lg text-[#222222]">{user?.partner_org_name || 'Partner Portal'}</div>
              </div>
            </div>
            <div className="flex items-center gap-4 overflow-x-auto text-sm">
              <Link to="/partner/dashboard" className="text-[#222222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Dashboard</Link>
              <Link to="/partner/opportunities" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Opportunities</Link>
              <Link to="/partner/community" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Community Board</Link>
              <Link to="/partner/participants" className="text-[#222222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Participants</Link>
              <Link to="/partner/invites" className="text-[#222222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Invites</Link>
              <Link to="/partner/staff" className="text-[#222222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Staff</Link>
              <Link to="/partner/time-entries" className="text-[#222222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Time Entries</Link>
              <Link to="/partner/reports" className="text-[#8B2E3C] font-bold whitespace-nowrap">Reports</Link>
              <button onClick={logout} className="text-[#8B2E3C] hover:underline whitespace-nowrap">Logout</button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#222222] mb-2">Reports</h1>
            <p className="text-sm text-[#666666]">
              Showing data for <span className="font-semibold text-[#8B2E3C]">{data?.organization_name || user?.partner_org_name}</span> only
            </p>
          </div>
          <button
            onClick={exportToCSV}
            disabled={loading || !data}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
            data-testid="export-csv-button"
          >
            <Download className="w-5 h-5" />
            Export CSV
          </button>
        </div>

        {/* Filters */}
        <div className="card mb-8">
          <div className="grid sm:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Report Type</label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="input-field"
                data-testid="report-type-select"
              >
                <option value="opportunities">Opportunities & Participants</option>
                <option value="hours">Hours & Attendance</option>
                <option value="time_entries">Time Entries (Check-in/out)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Start Date</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="input-field"
                data-testid="start-date-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">End Date</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="input-field"
                data-testid="end-date-input"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setDateRange({ start: '', end: '' })}
                className="btn-outline w-full"
                data-testid="clear-filters-button"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="card text-center">
            <Calendar className="w-8 h-8 text-[#8B2E3C] mx-auto mb-2" />
            <div className="text-2xl font-bold text-[#222222]">{summary.total_opportunities || 0}</div>
            <div className="text-sm text-[#666666]">Opportunities</div>
          </div>
          <div className="card text-center">
            <Users className="w-8 h-8 text-[#C9A227] mx-auto mb-2" />
            <div className="text-2xl font-bold text-[#222222]">{summary.filled_slots || 0} / {summary.total_slots || 0}</div>
            <div className="text-sm text-[#666666]">Slots Filled</div>
          </div>
          <div className="card text-center">
            <Clock className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-[#222222]">{summary.total_hours || 0}</div>
            <div className="text-sm text-[#666666]">Total Hours</div>
          </div>
          <div className="card text-center">
            <FileText className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-[#222222]">{summary.attended_count || 0}</div>
            <div className="text-sm text-[#666666]">Attended</div>
          </div>
          <div className="card text-center">
            <AlertTriangle className="w-8 h-8 text-red-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-[#222222]">{summary.no_show_count || 0}</div>
            <div className="text-sm text-[#666666]">No-Shows</div>
          </div>
        </div>

        {/* Report Tables */}
        {loading ? (
          <div className="text-center py-12 text-[#888]">Loading report data...</div>
        ) : !data ? (
          <div className="card text-center py-12 text-[#888]">No data available</div>
        ) : reportType === 'opportunities' ? (
          <div className="space-y-3">
            <h2 className="text-xl font-bold text-[#222222] mb-4">Opportunities & Participant Activity</h2>

            {data.opportunities.length === 0 ? (
              <div className="card text-center py-8 text-[#666666]">No opportunities found</div>
            ) : data.opportunities.map(opp => {
              const isExpanded = expandedOpps[opp.id];
              const fillRate = opp.capacity > 0 ? ((opp.signups_count / opp.capacity) * 100).toFixed(0) : 0;
              const hasParticipants = (opp.participants?.length || 0) + (opp.time_volunteers?.length || 0) > 0;
              return (
                <div key={opp.id} className="card" data-testid={`opp-report-${opp.id}`}>
                  {/* Opportunity Header — clickable to expand */}
                  <button
                    onClick={() => toggleOpp(opp.id)}
                    className="w-full text-left"
                    data-testid={`opp-expand-${opp.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="mt-1">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-[#8B2E3C]" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-[#888]" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-bold text-[#222] text-lg truncate">{opp.title}</div>
                          <div className="text-sm text-[#666] mt-0.5">
                            {opp.date} {opp.start_time && `| ${formatTimeRange(opp.start_time, opp.end_time)}`} {opp.town && `| ${opp.town}`}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 shrink-0 text-right">
                        <div>
                          <div className="text-xs text-[#888]">Signups</div>
                          <div className="font-bold text-[#222]">{opp.signups_count}/{opp.capacity}</div>
                        </div>
                        <div>
                          <div className="text-xs text-[#888]">Attended</div>
                          <div className="font-bold text-emerald-600">{opp.attended_count}</div>
                        </div>
                        <div>
                          <div className="text-xs text-[#888]">Hours</div>
                          <div className="font-bold text-[#8B2E3C]">{opp.total_hours}h</div>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          opp.status === 'closed' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
                        }`}>
                          {opp.status}
                        </span>
                      </div>
                    </div>
                  </button>

                  {/* Expanded: Participant Details */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-[#E9DDCF]">
                      {!hasParticipants ? (
                        <p className="text-sm text-[#888] text-center py-4">No participant activity for this opportunity</p>
                      ) : (
                        <div className="space-y-4">
                          {/* Signups Section */}
                          {opp.participants?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-bold text-[#444] mb-2 flex items-center gap-1.5">
                                <Users className="w-4 h-4" /> Sign-ups ({opp.participants.length})
                              </h4>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid={`opp-participants-${opp.id}`}>
                                  <thead>
                                    <tr className="border-b border-[#E9DDCF] text-[#888]">
                                      <th className="text-left py-2 px-3 font-medium">Volunteer</th>
                                      <th className="text-left py-2 px-3 font-medium">Email</th>
                                      <th className="text-left py-2 px-3 font-medium">Status</th>
                                      <th className="text-right py-2 px-3 font-medium">Hours</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {opp.participants.map((p, idx) => (
                                      <tr key={p.email || idx} className="border-b border-[#E9DDCF]/50 hover:bg-[#FBF4EA]">
                                        <td className="py-2 px-3 font-medium text-[#222]">{p.name}</td>
                                        <td className="py-2 px-3 text-[#666]">{p.email}</td>
                                        <td className="py-2 px-3">
                                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[p.signup_status] || 'bg-gray-100 text-gray-600'}`}>
                                            {p.signup_status?.replace('_', ' ')}
                                          </span>
                                        </td>
                                        <td className="py-2 px-3 text-right font-medium">
                                          {p.signup_status === 'attended' ? `${p.hours_credited || 0}h` : '-'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* Time Entries Section */}
                          {opp.time_volunteers?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-bold text-[#444] mb-2 flex items-center gap-1.5">
                                <Clock className="w-4 h-4" /> Time Entries ({opp.time_volunteers.length})
                              </h4>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid={`opp-time-${opp.id}`}>
                                  <thead>
                                    <tr className="border-b border-[#E9DDCF] text-[#888]">
                                      <th className="text-left py-2 px-3 font-medium">Volunteer</th>
                                      <th className="text-left py-2 px-3 font-medium">Check In</th>
                                      <th className="text-left py-2 px-3 font-medium">Check Out</th>
                                      <th className="text-left py-2 px-3 font-medium">Status</th>
                                      <th className="text-right py-2 px-3 font-medium">Hours</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {opp.time_volunteers.map((tv, idx) => (
                                      <tr key={tv.email || idx} className="border-b border-[#E9DDCF]/50 hover:bg-[#FBF4EA]">
                                        <td className="py-2 px-3 font-medium text-[#222]">{tv.name}</td>
                                        <td className="py-2 px-3 text-[#666]">
                                          {tv.check_in_time ? format(new Date(tv.check_in_time), 'MMM d, h:mm a') : '--'}
                                        </td>
                                        <td className="py-2 px-3 text-[#666]">
                                          {tv.check_out_time ? format(new Date(tv.check_out_time), 'MMM d, h:mm a') : '--'}
                                        </td>
                                        <td className="py-2 px-3">
                                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[tv.status] || 'bg-gray-100 text-gray-600'}`}>
                                            {tv.status}
                                          </span>
                                        </td>
                                        <td className="py-2 px-3 text-right font-medium">
                                          {tv.total_hours != null ? `${tv.total_hours.toFixed(1)}h` : '--'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* General Time Entries (walk-ins, not tied to a specific opportunity) */}
            {data.general_time_entries?.count > 0 && (
              <div className="card" data-testid="general-time-entries">
                <h3 className="font-bold text-[#222] text-lg mb-1">General Volunteering (Walk-ins)</h3>
                <p className="text-sm text-[#666] mb-3">
                  Time entries not tied to a specific opportunity | {data.general_time_entries.count} entries | {data.general_time_entries.total_hours}h total
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#E9DDCF] text-[#888]">
                        <th className="text-left py-2 px-3 font-medium">Volunteer</th>
                        <th className="text-left py-2 px-3 font-medium">Check In</th>
                        <th className="text-left py-2 px-3 font-medium">Check Out</th>
                        <th className="text-left py-2 px-3 font-medium">Status</th>
                        <th className="text-right py-2 px-3 font-medium">Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.general_time_entries.volunteers.map((tv, idx) => (
                        <tr key={tv.email || idx} className="border-b border-[#E9DDCF]/50 hover:bg-[#FBF4EA]">
                          <td className="py-2 px-3 font-medium text-[#222]">{tv.name}</td>
                          <td className="py-2 px-3 text-[#666]">
                            {tv.check_in_time ? format(new Date(tv.check_in_time), 'MMM d, h:mm a') : '--'}
                          </td>
                          <td className="py-2 px-3 text-[#666]">
                            {tv.check_out_time ? format(new Date(tv.check_out_time), 'MMM d, h:mm a') : '--'}
                          </td>
                          <td className="py-2 px-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[tv.status] || 'bg-gray-100 text-gray-600'}`}>
                              {tv.status}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-right font-medium">
                            {tv.total_hours != null ? `${tv.total_hours.toFixed(1)}h` : '--'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : reportType === 'hours' ? (
          <div className="card overflow-x-auto">
            <h2 className="text-xl font-bold text-[#222222] mb-4">Hours & Attendance Report</h2>
            {data.signups.length === 0 ? (
              <p className="text-center py-8 text-[#666666]">No signups found</p>
            ) : (
              <table className="w-full" data-testid="hours-table">
                <thead>
                  <tr className="border-b-2 border-[#E9DDCF]">
                    <th className="text-left py-3 px-4 font-semibold">Volunteer</th>
                    <th className="text-left py-3 px-4 font-semibold">Email</th>
                    <th className="text-left py-3 px-4 font-semibold">Opportunity</th>
                    <th className="text-left py-3 px-4 font-semibold">Date</th>
                    <th className="text-left py-3 px-4 font-semibold">Status</th>
                    <th className="text-right py-3 px-4 font-semibold">Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {data.signups.map((signup, idx) => (
                    <tr key={signup.id || idx} className="border-b border-[#E9DDCF] hover:bg-[#FBF4EA]">
                      <td className="py-3 px-4 font-medium">{signup.user_name}</td>
                      <td className="py-3 px-4 text-sm text-[#666666]">{signup.user_email}</td>
                      <td className="py-3 px-4 text-sm">{signup.opportunity_title}</td>
                      <td className="py-3 px-4 text-sm text-[#666666]">{signup.opportunity_date}</td>
                      <td className="py-3 px-4">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[signup.status] || 'bg-gray-100 text-gray-600'}`}>
                          {signup.status?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-medium">
                        {signup.status === 'attended' ? `${signup.hours_credited || 0}h` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <div className="card overflow-x-auto">
            <h2 className="text-xl font-bold text-[#222222] mb-4">Time Entries Report (Check-in/out)</h2>
            {data.time_entries.length === 0 ? (
              <p className="text-center py-8 text-[#666666]">No time entries found</p>
            ) : (
              <table className="w-full" data-testid="time-entries-table">
                <thead>
                  <tr className="border-b-2 border-[#E9DDCF]">
                    <th className="text-left py-3 px-4 font-semibold">Volunteer</th>
                    <th className="text-left py-3 px-4 font-semibold">Opportunity</th>
                    <th className="text-left py-3 px-4 font-semibold">Check In</th>
                    <th className="text-left py-3 px-4 font-semibold">Check Out</th>
                    <th className="text-left py-3 px-4 font-semibold">Status</th>
                    <th className="text-right py-3 px-4 font-semibold">Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {data.time_entries.map((entry, idx) => (
                    <tr key={entry.id || idx} className="border-b border-[#E9DDCF] hover:bg-[#FBF4EA]">
                      <td className="py-3 px-4 font-medium">{entry.volunteer_name}</td>
                      <td className="py-3 px-4 text-sm">{entry.opportunity_title || 'General'}</td>
                      <td className="py-3 px-4 text-sm text-[#666666]">
                        {entry.check_in_time ? format(new Date(entry.check_in_time), 'MMM d, h:mm a') : '--'}
                      </td>
                      <td className="py-3 px-4 text-sm text-[#666666]">
                        {entry.check_out_time ? format(new Date(entry.check_out_time), 'MMM d, h:mm a') : '--'}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[entry.status] || 'bg-gray-100 text-gray-600'}`}>
                          {entry.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-medium">
                        {entry.total_hours != null ? `${entry.total_hours.toFixed(1)}h` : '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerReports;
